/**
 * A planta do jardim desenhada à mão, como no Flutter.
 *
 * Os cinco desenhos moraram aqui até o celular também precisar deles; agora
 * estão no `shared`, em `jardim/pinceis`, e este arquivo é só o desenhista do
 * navegador: prepara o canvas, faz a planta respirar e entrega o contexto.
 *
 * O laço de quadros é `requestAnimationFrame` mesmo: a regra de não escrever
 * animação à mão vale para o que o navegador anima sozinho (transições, layout,
 * gestos), e não para um desenho que precisa ser repintado a cada quadro. Quem
 * pediu menos movimento recebe a planta parada, no meio da respiração.
 */

import { useEffect, useRef } from 'react';
import {
  FOLEGO,
  PINCEIS,
  RESPIRO_PARADO,
  type Pincel2D,
  type Planta as Especie,
} from '@nosso-sempre/shared';

export function Planta({
  especie,
  fase,
  tamanho,
}: {
  especie: Especie;
  fase: number;
  tamanho: number;
}) {
  const tela = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = tela.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // O contexto do navegador faz tudo o que o `Pincel2D` pede, mas não é
    // atribuível a ele: `fillStyle` aceita `CanvasPattern` além de degradê, e
    // propriedade que se escreve não aceita tipo mais estreito. Nenhum dos
    // desenhos lê `fillStyle`, então a conversão é segura.
    const pincel = ctx as unknown as Pincel2D;

    // A tela do navegador tem mais pixels que pontos; sem isto a planta sai
    // borrada nos aparelhos bons, que são justamente os que a mostram grande.
    const escala = window.devicePixelRatio || 1;
    canvas.width = Math.round(tamanho * escala);
    canvas.height = Math.round(tamanho * escala);

    const parado = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let pedido = 0;
    let comeco = 0;

    function quadro(agora: number) {
      if (!comeco) comeco = agora;
      // Vai e volta: o `repeat(reverse: true)` do controlador de lá.
      const volta = ((agora - comeco) % (FOLEGO * 2)) / FOLEGO;
      const respiro = parado ? RESPIRO_PARADO : volta <= 1 ? volta : 2 - volta;

      ctx!.setTransform(escala, 0, 0, escala, 0, 0);
      ctx!.clearRect(0, 0, tamanho, tamanho);
      PINCEIS[especie.id]?.(pincel, tamanho, fase, respiro);

      if (!parado) pedido = requestAnimationFrame(quadro);
    }

    pedido = requestAnimationFrame(quadro);
    return () => cancelAnimationFrame(pedido);
  }, [especie.id, fase, tamanho]);

  return (
    <canvas
      ref={tela}
      className="planta"
      style={{ width: tamanho, height: tamanho }}
      role="img"
      aria-label={`${especie.displayName}, ${especie.stageDescriptions[Math.min(5, Math.max(0, fase))]}`}
    />
  );
}
